"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface Props {
  src?: string;
  alt?: string;
}

export default function BlogImage({ src, alt = "" }: Props) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  if (!src) return null;

  return (
    <>
      <span className="my-6 block">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group relative mx-auto block cursor-zoom-in bg-transparent p-0"
          aria-label={alt ? `Open full size: ${alt}` : "Open full size image"}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className="mx-auto block max-h-[360px] w-auto object-contain transition-transform group-hover:scale-[1.01]"
          />
          <span className="pointer-events-none absolute right-2 top-2 rounded-md border border-white/15 bg-black/50 px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-white/90 opacity-0 transition-opacity group-hover:opacity-100">
            Click to expand
          </span>
        </button>
        {alt && (
          <span className="mt-2 block text-center text-xs text-text-muted">
            {alt}
          </span>
        )}
      </span>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={alt || "Image preview"}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              className="absolute right-4 top-4 z-10 rounded-full border border-white/20 bg-black/50 p-2 text-white transition-colors hover:bg-black/70"
              aria-label="Close preview"
            >
              <X size={20} />
            </button>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={src}
              alt={alt}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[92vh] max-w-[95vw] rounded-xl object-contain shadow-2xl"
            />
          </div>,
          document.body,
        )}
    </>
  );
}
